<script setup lang="ts">
/**
 * A forced page break, and the page it opens.
 *
 * The break itself is invisible in print and visible while editing: a page
 * break somebody cannot see is one they will insert twice. How it is shown
 * depends on where it stands (see `between.ts`).
 *
 * **Between two sheets**, it is the seam, so it is drawn as a tab sitting in
 * it: small enough not to compete with the paper on either side, and the thing
 * to click to set the page's picture or to pick the break up and move it. A
 * swatch on the tab says the page it opens has a picture, since the sheet
 * showing that picture may be scrolled out of view. The picture itself is
 * drawn by that sheet.
 *
 * **Without sheets around it**, as on a specimen page, there is no seam to sit
 * in, so it is a dashed rule across the text and lays its picture into the
 * flow after it.
 */
import { useI18n } from 'vue-i18n'

import { computed, inject } from 'vue'

import { backgroundStyle, hasBackground } from '@/domain/layout/background'
import type { PageBreakSection } from '@/domain/section'
import PageBackdrop from '@/sections/pageBreak/PageBackdrop.vue'
import { BETWEEN_SHEETS } from '@/sections/pageBreak/between'

const { t } = useI18n()

const props = defineProps<{ section: PageBreakSection }>()

const between = inject(BETWEEN_SHEETS, false)

const painted = computed(() => hasBackground(props.section.background))
const swatch = computed(() => backgroundStyle(props.section.background))
</script>

<template>
  <div v-if="between" class="tab page-break" role="separator" :aria-label="t('sections.pageBreak')">
    <svg class="tab__icon" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3.5 2.5v3.5h9V2.5M2 8h12M3.5 13.5V10h9v3.5"
        fill="none"
        stroke="currentColor"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <span>{{ t('sections.pageBreak') }}</span>
    <span v-if="section.spread" class="tab__spread">{{ t('canvas.spread') }}</span>
    <span v-if="painted" class="tab__swatch" :style="swatch" aria-hidden="true" />
  </div>

  <template v-else>
    <div class="break page-break" role="separator" :aria-label="t('sections.pageBreak')">
      <span class="break__label">{{ t('sections.pageBreak') }}</span>
    </div>

    <PageBackdrop v-if="painted" :background="section.background" placement="flow" />
  </template>
</template>

<style scoped>
.tab {
  display: inline-flex;
  align-items: center;
  gap: 2mm;
  height: 7mm;
  padding: 0 3mm;
  border-radius: 999px;
  background: var(--paper);
  box-shadow: 0 0 0 1px var(--paper-rule);
  color: var(--paper-ink-soft);
  font-family: var(--font-ui);
  font-size: 7.5pt;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.tab__icon {
  flex: none;
}

/* Said on the tab as well as shown by the sheet, since the sheet may be
   scrolled out of view and a double page changes what goes on it. */
.tab__spread {
  padding: 0.4mm 1.6mm;
  border-radius: 999px;
  background: var(--page-accent);
  color: var(--paper);
  font-size: 6.5pt;
  letter-spacing: 0.06em;
}

/* The picture itself, shrunk to a stamp. Drawn with the same properties the
   page uses, so what the swatch shows is what the page shows. */
.tab__swatch {
  width: 4mm;
  height: 5mm;
  border-radius: 1px;
  box-shadow: 0 0 0 1px var(--paper-rule);
  background-image: var(--page-bg-image);
  background-size: cover;
  background-position: center;
  opacity: max(var(--page-bg-strength, 1), 0.5);
}

.break {
  display: flex;
  align-items: center;
  gap: 3mm;
  margin: 6mm 0;
  color: var(--paper-ink-soft);
  font-family: var(--font-ui);
  font-size: 8pt;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.break::before,
.break::after {
  content: '';
  flex: 1;
  border-top: 1px dashed var(--paper-rule);
}

@media print {
  .tab {
    display: none;
  }

  /* Present but empty, rather than removed: an element that is not displayed
     cannot end a page, and ending a page is all this is for once printed. */
  .break {
    display: block;
    height: 0;
    margin: 0;
    overflow: hidden;
    visibility: hidden;
  }
}
</style>
